import { ZdateTime } from '../common.zod';
import { z } from 'zod';

// TechlogDefectCreateInput Zod schema based on the TypeScript interface
export const ZTechlogDefectCreateInput = z.object({
  itemDescription: z
    .string()
    .min(1)
    .describe('Short description of the defect item (max 35 chars)'),
  details: z.string().min(1).describe('Detailed description of the defect'),
  raisedAt: z.string().describe('Date when the defect was raised (ISO string)'),
  ataChapter: z.string().describe('ATA chapter code (e.g., "25", "32")'),
  ataSection: z.string().describe('ATA section code (e.g., "10", "21")'),
  deferred: z.boolean().describe('Whether the defect is deferred'),
  defectType: z.string().describe('Type of defect (e.g., "MEL", "CDL", "NEF")'),
  rectificationCategory: z
    .string()
    .describe('Rectification category (e.g., "DEFERRED", "NONDEFERRED")'),
  reference: z
    .string()
    .optional()
    .describe('External reference (e.g., CAMP defect number)'),
  thirdPartyReferences: z
    .record(z.any())
    .optional()
    .describe('Third party integration references'),

  // Optional fields that may be included in defect creation
  dueDate: z
    .string()
    .optional()
    .describe('Due date for rectification (ISO string)'),
  limitations: z
    .string()
    .optional()
    .describe('Operational limitations imposed by the defect'),
  limitFlightSeconds: z
    .number()
    .optional()
    .describe('Flight time limit in seconds'),
  limitApuSeconds: z.number().optional().describe('APU time limit in seconds'),
  limitCycles: z.number().optional().describe('Cycle limit'),
  limitCalendarDays: z.number().optional().describe('Calendar day limit'),
});

// Deferral-specific subset of DefectBodyv1 for PUT /v1/defects/{id}.
// Represents the fields accepted when deferring a defect via the TechLog API.
export const ZTechlogDefectDeferInput = z.object({
  deferred: z.literal(true),
  rectificationCategory: z.enum([
    'A',
    'B',
    'C',
    'D',
    'NONDEFERRED',
    'ADVISORYONLY',
  ]),
  limitations: z.string().optional(),
  limitFlightSeconds: z.number().int().optional(),
  limitSeconds: z.number().int().optional(),
  limitFlightDays: z.number().int().optional(),
  limitCycles: z.number().int().optional(),
  limitFlights: z.number().int().optional(),
  limitCalendarDays: z.number().int().optional(),
  limitApuSeconds: z.number().int().optional(),
  limitOther: z.string().optional(),
  deferApprovalReference: z.string().optional(),
  reasonToDefer: z.string().optional(),
  deferredAt: ZdateTime.optional(),
  deferralLicenceNumber: z.string().optional(),
  deferralApprovalNumber: z.string().optional(),
  deferredById: z.string().uuid().optional(),
  deferUsing: z.string().optional(),
});

// TechlogDefectResponse Zod schema based on the TechlogDefectResponse interface
// This matches the structure returned by TechlogAPI.fetchDefectById()
export const ZTechlogDefectResponse = z.object({
  id: z.string().describe('TechLog defect ID'),
  aircraftId: z.string().describe('TechLog aircraft ID'),
  sectorId: z.string().nullable().describe('Associated sector/flight ID'),
  reportAirportId: z
    .number()
    .nullable()
    .describe('Airport where defect was reported'),
  resolutionAirportId: z
    .number()
    .nullable()
    .describe('Airport where defect was resolved'),
  details: z.string().describe('Detailed description of the defect'),
  itemDescription: z
    .string()
    .nullable()
    .describe('Short description of the defect item'),
  number: z.number().describe('TechLog defect number'),
  rectificationIntervalExtension: z
    .any()
    .describe('Rectification interval extension'),
  dueDate: z.string().nullable().describe('Due date for rectification'),
  dueDateTime: ZdateTime.nullable()
    .optional()
    .describe('Due date/time for rectification'),
  estimatedDate: z
    .string()
    .nullable()
    .optional()
    .describe('Estimated completion date'),
  raisedAt: z.string().describe('Date when the defect was raised'),
  dueFlightSeconds: z
    .number()
    .nullable()
    .optional()
    .describe('Flight time limit in seconds'),
  dueApuSeconds: z
    .number()
    .nullable()
    .optional()
    .describe('APU time limit in seconds'),
  dueCycles: z.number().nullable().optional().describe('Cycle limit'),
  dueLandings: z.number().nullable().optional().describe('Landing limit'),
  status: z.string().describe('Current defect status'),
  reference: z.string().nullable().describe('External reference'),
  ataChapter: z.number().nullable().optional().describe('ATA chapter code'),
  ataSection: z.number().nullable().optional().describe('ATA section code'),
  flightsDue: z
    .number()
    .nullable()
    .optional()
    .describe('Number of flights due'),
  limitations: z.string().nullable().describe('Operational limitations'),
  melItemId: z.string().nullable().describe('MEL item ID'),
  melRectificationId: z.string().nullable().describe('MEL rectification ID'),
  placardProcedureCompleted: z
    .boolean()
    .nullable()
    .describe('Placard procedure completion status'),
  operationalProcedureCompleted: z
    .boolean()
    .nullable()
    .describe('Operational procedure completion status'),
  deferred: z.boolean().describe('Whether the defect is deferred'),
  raisedFromId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of source that raised the defect'),
  // Additional fields used by transformation schemas and mocks
  reportedById: z
    .string()
    .nullable()
    .optional()
    .describe('User ID who reported the defect'),
  reportedByType: z
    .string()
    .nullable()
    .optional()
    .describe('Type of reporter (pilot, maintenance, cabin)'),
  defectType: z.string().nullable().optional().describe('Type of defect'),
  rectificationCategory: z
    .string()
    .nullable()
    .optional()
    .describe('Rectification category'),
  troubleshootingSteps: z
    .string()
    .nullable()
    .optional()
    .describe('Troubleshooting steps taken'),
  reasonToDefer: z
    .string()
    .nullable()
    .optional()
    .describe('Reason for deferral'),
  deferApprovalReference: z
    .string()
    .nullable()
    .optional()
    .describe('Deferral approval reference'),
  deferredAt: ZdateTime.nullable()
    .optional()
    .describe('When the defect was deferred'),
  deferredById: z
    .string()
    .nullable()
    .optional()
    .describe('User ID who deferred the defect'),
  deferralLicenceNumber: z
    .string()
    .nullable()
    .optional()
    .describe('Deferral licence number'),
  deferralApprovalNumber: z
    .string()
    .nullable()
    .optional()
    .describe('Deferral approval number'),
  deferralSignatureId: z
    .string()
    .nullable()
    .optional()
    .describe('Deferral signature ID'),
  limitFlightSeconds: z
    .number()
    .nullable()
    .optional()
    .describe('Flight time limit in seconds'),
  limitSeconds: z
    .number()
    .nullable()
    .optional()
    .describe('Time limit in seconds'),
  limitFlightDays: z
    .number()
    .nullable()
    .optional()
    .describe('Flight day limit'),
  limitCycles: z.number().nullable().optional().describe('Cycle limit'),
  limitFlights: z.number().nullable().optional().describe('Flight limit'),
  limitCalendarDays: z
    .number()
    .nullable()
    .optional()
    .describe('Calendar day limit'),
  limitApuSeconds: z
    .number()
    .nullable()
    .optional()
    .describe('APU time limit in seconds'),
  limitOther: z.string().nullable().optional().describe('Other limits'),
  updatedAt: ZdateTime.nullable().optional().describe('Last update timestamp'),
  createdAt: ZdateTime.nullable().optional().describe('Creation timestamp'),
  source: z.string().nullable().optional().describe('Source of the defect'),
  importedAt: ZdateTime.nullable().optional().describe('Import timestamp'),
  tripEntryId: z.string().nullable().optional().describe('Trip entry ID'),
  serviceabilityLimiting: z
    .boolean()
    .nullable()
    .optional()
    .describe('Serviceability limiting'),
  editedById: z
    .string()
    .nullable()
    .optional()
    .describe('User ID who edited the defect'),
  editedDate: ZdateTime.nullable().optional().describe('Edit date'),
  eropsPrevented: z.boolean().nullable().optional().describe('EROPS prevented'),
  placardNumber: z.number().nullable().optional().describe('Placard number'),
  campSyncDate: ZdateTime.nullable().optional().describe('CAMP sync date'),
  campReference: z.string().nullable().optional().describe('CAMP reference'),
  intermittentFaultId: z
    .string()
    .nullable()
    .optional()
    .describe('Intermittent fault ID'),
  deferUsing: z.string().nullable().optional().describe('Defer using'),
  thirdPartyReferences: z
    .record(z.any())
    .nullable()
    .optional()
    .describe('Third party references'),
});

// TechlogDefectResolution Zod schema based on the TechlogDefectResolution interface
// This matches the structure returned by TechlogAPI.fetchResolutionById()
export const ZTechlogDefectResolution = z.object({
  id: z.string().describe('Resolution ID'),
  mxItemId: z.string().describe('MX item ID'),
  date: z.string().describe('Resolution date'),
  description: z
    .string()
    .nullable()
    .optional()
    .describe('Resolution notes/description'),
  limitations: z.string().nullable().optional().describe('Limitations'),
  workType: z.string().nullable().optional().describe('Type of work performed'),
  mxItemResolved: z
    .boolean()
    .nullable()
    .optional()
    .describe('Whether MX item is resolved'),
  tripEntryId: z.string().nullable().optional().describe('Trip entry ID'),
  partChanges: z.array(z.any()).nullable().optional().describe('Part changes'),
  releaseId: z.string().nullable().optional().describe('Release ID'),
  damageReportId: z.string().nullable().optional().describe('Damage report ID'),
  approvalNumber: z.string().nullable().optional().describe('Approval number'),
  companyName: z.string().nullable().optional().describe('Company name'),
  firstName: z
    .string()
    .nullable()
    .optional()
    .describe('First name of resolver'),
  lastName: z.string().nullable().optional().describe('Last name of resolver'),
  licenceNumber: z.string().nullable().optional().describe('Licence number'),
  signatureId: z.string().nullable().optional().describe('Signature ID'),
  customData: z.record(z.any()).nullable().optional().describe('Custom data'),
  createdAt: z.string().describe('Creation timestamp'),
  updatedAt: z.string().describe('Last update timestamp'),
});

// Export the inferred types
export type TechlogDefectCreateInputZod = z.infer<
  typeof ZTechlogDefectCreateInput
>;
export type TechlogDefectDeferInputZod = z.infer<
  typeof ZTechlogDefectDeferInput
>;
export type TechlogDefectResponseZod = z.infer<typeof ZTechlogDefectResponse>;
export type TechlogDefectResolutionZod = z.infer<
  typeof ZTechlogDefectResolution
>;
